import type { ReactNode } from "react";
import { ArrowRightIcon, RotateCwIcon, XIcon } from "lucide-react";
import { useCurrentRefinements, useInstantSearch } from "react-instantsearch";

import { cn } from "@tudigo-monorepo/core-tudigo-theme";
import { useTranslation } from "@tudigo-monorepo/core-tudigo-translations";
import { ButtonRebrand } from "@tudigo-monorepo/web-tudigo-components";

import { useSearchProjectsContext } from "../../state/search-projects.context";
import { CustomClearRefinements } from "./custom-clear-refinements";
import { SearchProjectsRefinements } from "./search-projects-refinements";

export function SearchProjectsRefinementsMenu(props: { children?: ReactNode }) {
  const { isRefinementsMenuOpen, setIsRefinementsMenuOpen } =
    useSearchProjectsContext();
  const { results } = useInstantSearch();
  const { canRefine } = useCurrentRefinements();
  const { t } = useTranslation();

  const handleCloseMenu = () => setIsRefinementsMenuOpen(false);

  if (!isRefinementsMenuOpen) {
    return null;
  }

  return (
    <div
      className={cn(
        "fixed left-0 top-0 flex h-full min-h-dvh w-screen flex-col overflow-y-auto bg-white md:hidden",
        `z-popover`,
      )}
    >
      <header className="border-medium-2 flex min-h-12 items-center justify-between border-b">
        <h3 className="text-primary py-1.5 pl-4 font-sans text-lg font-bold">
          {t("search_projects_page.refinements.menu.header.title")}
        </h3>
        <button onClick={handleCloseMenu} className="h-full px-4">
          <XIcon className="text-dark-2 h-6 w-6" />
          <span className="sr-only">Close</span>
        </button>
      </header>

      <main className="bg-black-400 flex h-full flex-col gap-y-6 p-4">
        {props.children}
        <SearchProjectsRefinements className="pb-52" />
      </main>

      <footer className="border-medium-2 fixed bottom-0 left-0 flex w-full items-center justify-center gap-x-4 border-t bg-white p-4">
        <CustomClearRefinements className="w-1/2">
          <RotateCwIcon className="text-accent h-6 w-6" />
          <p className="text-accent font-sans text-sm font-semibold">
            {t(`search_projects_page.refinements.reset.cta.label`)}
          </p>
        </CustomClearRefinements>

        <ButtonRebrand
          onClick={handleCloseMenu}
          variant="primary"
          className={cn(
            "bg-primary-rebrand flex w-full items-center justify-center gap-x-2 font-sans text-sm font-medium",
            {
              "w-1/2": canRefine,
            },
          )}
        >
          {t("search_projects_page.projects_count", { count: results.nbHits })}
          <ArrowRightIcon className="h-6 w-6" />
        </ButtonRebrand>
      </footer>
    </div>
  );
}
